import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NewSolution } from '../features/NewSolutionXPLR/SolutionXPLRInterfaces';

const initialState: NewSolution = {
  opportunity_statement: '',
  current_process: '',
  proposed_enhancements: '',
  enterprise_applications_in_use: [],
  other_software: '',
  delivery_preferences: '',
  estimated_impact: '',
  timeline: '',
  uploaded_files: [],
  is_draft: true,
  is_submitted: false,
  submitted_at: null,
  created_at: '',
  updated_at: '',
  data_assets: [],
  updated_by_id: '',
  updated_by_name: '',
  organization_id: '',
  solution_name: '',
  process_status: '',
  markdown_file_status: '',
  opportunity_report_status: '',
  approval_state: {
    targeted_process: 'UNAPPROVED',
    agentic_workflow: 'UNAPPROVED',
    feasibility_and_impact: 'UNAPPROVED',
    ai_solution_blueprint: 'UNAPPROVED',
    elaborated_summary: 'UNAPPROVED',
    solution_requirements: 'UNAPPROVED',
    data_sources: 'UNAPPROVED',
  },
  opportunity_report: {
    data_sources: [],
    form_data: {},
    benefit_values: {},
    general_data: {},
    poc_cost_range: '',
    scaled_cost_range: '',
    maintenance_cost_range: '',
    maintenance_plan: '',
    page_title: '',
    total_benefits: { yr_1: 0, yr_3: 0, yr_5: 0 },
    roi: { yr_1: 0, yr_3: 0, yr_5: 0 },
    proposed_ai_solution_file: [], // Make sure this exists in initial state
  },
  dataSources: {
    internal: [],
    external: [],
  },
  bpmnData: null,
  current_data_sources: [],
  additional_data_sources: [],
  proposed_ai_solution_file: [],
  elaborated_documents: [],
  current_process_bpmn_mapping: {
    isBPMNDiagram: false,
    xml_string: '',
    xml_data: null,
    subprocessName: '',
    functionName: '',
    e2eName: '',
    processName: '',
  },
  current_process_generate_process: {
    url: '',
    description: '',
    name: '',
  },
  use_case_id: null,
  opportunity_id: null,
};

// Interface for document update payload
interface UpdateDocumentPayload {
  documentId: string;
  updates: {
    content?: string;
    updated_at?: string;
    file_size?: number;
    updated_by_id?: string;
    updated_by_name?: string;
    [key: string]: any;
  };
}

// Interface for proposed AI solution update
interface UpdateProposedAiSolutionPayload {
  content: string;
  markdown_content?: string;
  updated_at?: string;
  updated_by_id?: string;
  updated_by_name?: string;
  fileIndex?: number; // In case there are multiple files
}

const solutionXPLRSlice = createSlice({
  name: 'solutionXPLR',
  initialState,
  reducers: {
    updateSolution(state, action: PayloadAction<NewSolution>) {
      return { ...state, ...action.payload };
    },
    resetSolution() {
      return initialState;
    },
    updateDataSources: (state, action) => {
      state.data_assets = action.payload.allDataSources;
      state.bpmnData = action.payload.bpmnFiles;
    },
    modifyDataSources: (state, action: PayloadAction<any[]>) => {
      if (!state.opportunity_report) {
        state.opportunity_report = {
          data_sources: [],
          form_data: {},
          benefit_values: {},
          general_data: {},
          poc_cost_range: '',
          scaled_cost_range: '',
          maintenance_cost_range: '',
          maintenance_plan: '',
          total_benefits: { yr_1: 0, yr_3: 0, yr_5: 0 },
          roi: { yr_1: 0, yr_3: 0, yr_5: 0 },
          proposed_ai_solution_file: [],
        };
      }
      state.opportunity_report.data_sources = action.payload;
    },

    updateCostData: (
      state,
      action: PayloadAction<
        Partial<{
          poc_cost_range: string;
          scaled_cost_range: string;
          maintenance_cost_range: string;
          maintenance_plan: string;
          benefit_values: any;
          proposed_ai_solution_file: any[];
        }>
      >
    ) => {
      if (!state.opportunity_report) return;

      state.opportunity_report = {
        ...state.opportunity_report,
        ...action.payload,
      };
    },

    // New specific reducer for updating proposed AI solution
    updateProposedAiSolution: (state, action: PayloadAction<UpdateProposedAiSolutionPayload>) => {
      const {
        content,
        markdown_content,
        updated_at,
        updated_by_id,
        updated_by_name,
        fileIndex = 0,
      } = action.payload;
      // Ensure opportunity_report exists
      if (!state.opportunity_report) {
        state.opportunity_report = {
          data_sources: [],
          form_data: {},
          benefit_values: {},
          general_data: {},
          poc_cost_range: '',
          scaled_cost_range: '',
          maintenance_cost_range: '',
          maintenance_plan: '',
          total_benefits: { yr_1: 0, yr_3: 0, yr_5: 0 },
          roi: { yr_1: 0, yr_3: 0, yr_5: 0 },
          proposed_ai_solution_file: [],
        };
      }

      // Ensure proposed_ai_solution_file array exists
      if (!Array.isArray(state.opportunity_report.proposed_ai_solution_file)) {
        state.opportunity_report.proposed_ai_solution_file = [];
      }

      // Create or update the file at the specified index
      if (state.opportunity_report.proposed_ai_solution_file[fileIndex]) {
        // Update existing file
        state.opportunity_report.proposed_ai_solution_file[fileIndex] = {
          ...state.opportunity_report.proposed_ai_solution_file[fileIndex],
          content,
          markdown_content: markdown_content || content,
          updated_at: updated_at || new Date().toISOString(),
        };
      } else {
        // Create new file entry
        state.opportunity_report.proposed_ai_solution_file[fileIndex] = {
          id: `proposed_ai_solution_${fileIndex}`,
          content,
          markdown_content: markdown_content || content,
          file_name: 'proposed_ai_solution.md',
          file_type: 'markdown',
          created_at: new Date().toISOString(),
          updated_at: updated_at || new Date().toISOString(),
        };
      }

      // Update solution-level metadata
      state.updated_at = updated_at || new Date().toISOString();
      if (updated_by_id) {
        state.updated_by_id = updated_by_id;
      }
      if (updated_by_name) {
        state.updated_by_name = updated_by_name;
      }
    },

    // Original action for updating elaborated documents
    updateSolutionDocument: (state, action: PayloadAction<UpdateDocumentPayload>) => {
      const { documentId, updates } = action.payload;
      // Initialize elaborated_documents if it doesn't exist
      if (!Array.isArray(state.elaborated_documents)) {
        state.elaborated_documents = [];
      }
      // Find and update the specific document in elaborated_documents array
      const documentIndex = state.elaborated_documents.findIndex(
        (doc) => doc && typeof doc === 'object' && doc.id === documentId
      );
      if (documentIndex >= 0) {
        // Ensure the document exists before updating
        const existingDocument = state.elaborated_documents[documentIndex];
        if (existingDocument) {
          // Update the document with new data
          state.elaborated_documents[documentIndex] = {
            ...existingDocument,
            ...updates,
          };
          // Also update solution's overall updated_at timestamp
          if (updates.updated_at) {
            state.updated_at = updates.updated_at;
          }
          // Update updated_by information if provided
          if (updates.updated_by_name) {
            state.updated_by_name = updates.updated_by_name;
          }
          if (updates.updated_by_id) {
            state.updated_by_id = updates.updated_by_id;
          }
        }
      } else {
        // Document not found, optionally create a new one or log warning
        console.warn(`Document with ID ${documentId} not found in elaborated_documents`);
      }
    },
  },
});

export const {
  updateSolution,
  resetSolution,
  updateDataSources,
  modifyDataSources,
  updateSolutionDocument,
  updateProposedAiSolution, // New action export
  updateCostData,
} = solutionXPLRSlice.actions;

export default solutionXPLRSlice.reducer;
